import { notifySignOut, webHeaders } from "../config";
import { acceptTokens, stopRefreshTimer } from "./refresh";
import { clearSession, getSession } from "./session";

export interface AuthResult {
  access_token: string;
  expires_in: number;
  user: { id: string; email: string; must_change_password: boolean };
}

export class AuthError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

/** POST to an auth endpoint as the first-party web app (cookie mode). */
export async function postAuth<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      ...webHeaders(),
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new AuthError(
      res.status,
      String(data.code ?? "error"),
      String(data.message ?? "Something went wrong. Please try again."),
    );
  }
  return data as T;
}

/** Stores the session from a sign-in response. */
export function acceptAuthResult<T extends AuthResult>(result: T): T {
  acceptTokens(result.access_token, result.expires_in);
  return result;
}

export async function login(email: string, password: string): Promise<AuthResult> {
  return acceptAuthResult(await postAuth<AuthResult>("/v1/auth/login", { email, password }));
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await postAuth(
    "/v1/auth/password",
    { current_password: currentPassword, new_password: newPassword },
    getSession()?.accessToken,
  );
}

export async function logout(): Promise<void> {
  const session = getSession();
  try {
    if (session) await postAuth("/v1/auth/logout", {}, session.accessToken);
  } finally {
    stopRefreshTimer();
    clearSession();
    notifySignOut();
  }
}
